/**
 * Módulo Admin (`/admin`, solo Admin).
 *
 * Orquesta cuatro bloques y no dibuja datos: usuarios, catálogo de métricas,
 * fuentes y config de deep links. La pantalla se ordena por urgencia como el
 * resto del panel — la TriageBar de arriba dice qué está roto y cada conteo baja
 * al bloque donde se arregla.
 *
 * El 403 lo decide el servidor: `GET /api/admin` es `requireRole('admin')`, y
 * esconder el link en la sidebar es cortesía, no seguridad.
 */
import type { DeepLinkConfig, MetricDefinition } from '@ops/contracts'
import { useMemo, useState } from 'react'

import { ApiError } from '../../lib/api'
import { configFromServer } from '../../lib/deeplinks'
import { plural } from '../../lib/format'
import { irA } from '../../lib/scroll'
import { useAppConfigQuery } from '../../lib/queries'
import { TriageBar, type Severidad, type TriageConteo } from '../../ui'
import { Cargando, ErrorDeCarga } from '../comunes'
import { CatalogoPanel, severidadDeMetrica } from './CatalogoPanel'
import { DeepLinksPanel } from './DeepLinksPanel'
import { FuentesPanel } from './FuentesPanel'
import { MetricaDialog } from './MetricaDialog'
import { UsuariosPanel } from './UsuariosPanel'
import { camposVacios } from './configCampos'
import { useAdmin } from './queries'

const ID_USUARIOS = 'admin-usuarios'
const ID_CATALOGO = 'admin-catalogo'
const ID_FUENTES = 'admin-fuentes'
const ID_DEEP_LINKS = 'admin-deep-links'

type Dialogo =
  | { modo: 'cerrado' }
  | { modo: 'alta' }
  | { modo: 'edicion'; metrica: MetricDefinition }

export default function AdminPage() {
  const admin = useAdmin()
  const config = useAppConfigQuery()
  const [ahora] = useState(() => new Date())
  const [dialogo, setDialogo] = useState<Dialogo>({ modo: 'cerrado' })

  const cfgDeepLinks: DeepLinkConfig = useMemo(
    () => configFromServer(config.data?.deepLinks),
    [config.data],
  )

  if (admin.isLoading) {
    return <Cargando que="usuarios, catálogo y fuentes" />
  }

  if (admin.error !== null || admin.data === undefined) {
    return (
      <ErrorDeCarga
        que="el módulo Admin"
        error={admin.error}
        // Un 403 acá no es un fallo de lectura sino una respuesta: cambia el
        // título, explica qué administra la sección y **no** ofrece reintentar,
        // porque apretar el botón diez veces no cambia el rol de nadie.
        {...(!(admin.error instanceof ApiError && admin.error.esPermiso)
          ? { onRetry: () => void admin.refetch() }
          : {
              titulo: 'Sin acceso al módulo Admin',
              nota: (
                <p className="max-w-prosa text-meta text-muted-strong">
                  Admin administra usuarios, el catálogo de métricas y las fuentes
                  de datos. Si necesitás entrar, pedile a un admin que te cambie
                  el rol.
                </p>
              ),
            })}
      />
    )
  }

  const { usuarios, metricas, fuentes } = admin.data

  const fuentesConError = fuentes.filter((f) => f.status === 'error').length
  const fuentesSinConectar = fuentes.filter((f) => f.status === 'manual').length
  const metricasSinSemaforo = metricas.filter(
    (m) => severidadDeMetrica(m) === 'critical',
  ).length
  const metricasSinDueno = metricas.filter((m) => m.ownerId === null).length
  const deepLinksVacios = camposVacios(cfgDeepLinks).length

  // Un conteo en cero no se dibuja: la barra de triage lista lo que hay que
  // resolver, y cinco ceros alineados esconden el único número que importa.
  //
  // Los cinco pasan por `plural`, el mismo helper que usan los chips del
  // Overview. Con la etiqueta cableada en plural, el único conteo que en la app
  // vale 1 decía "1 campos de deep link sin configurar"; y como el filtro de
  // abajo saca los ceros, cualquiera de los otros cuatro podía caer en lo mismo.
  const todosLosConteos: TriageConteo[] = [
    {
      severidad: 'critical',
      cantidad: fuentesConError,
      label: plural(fuentesConError, 'fuente con error', 'fuentes con error'),
      onClick: () => irA(ID_FUENTES),
    },
    {
      severidad: 'critical',
      cantidad: metricasSinSemaforo,
      label: plural(
        metricasSinSemaforo,
        'métrica sin umbral',
        'métricas sin umbral',
      ),
      onClick: () => irA(ID_CATALOGO),
    },
    {
      severidad: 'warning',
      cantidad: deepLinksVacios,
      label: plural(
        deepLinksVacios,
        'campo de deep link sin configurar',
        'campos de deep link sin configurar',
      ),
      onClick: () => irA(ID_DEEP_LINKS),
    },
    {
      severidad: 'warning',
      cantidad: fuentesSinConectar,
      label: plural(
        fuentesSinConectar,
        'fuente en carga manual',
        'fuentes en carga manual',
      ),
      onClick: () => irA(ID_FUENTES),
    },
    {
      severidad: 'warning',
      cantidad: metricasSinDueno,
      label: plural(metricasSinDueno, 'métrica sin dueño', 'métricas sin dueño'),
      onClick: () => irA(ID_CATALOGO),
    },
  ]
  const conteos = todosLosConteos.filter((conteo) => conteo.cantidad > 0)

  const criticos = fuentesConError + metricasSinSemaforo
  const atencion = deepLinksVacios + fuentesSinConectar + metricasSinDueno
  const estado: Severidad =
    criticos > 0 ? 'critical' : atencion > 0 ? 'warning' : 'ok'

  return (
    <>
      {/* Sangra el padding del `main` para que la barra llegue a los bordes:
          es una barra de estado de la pantalla, no una tarjeta más. */}
      <TriageBar
        estado={estado}
        titular={titularDe(criticos, atencion)}
        conteos={conteos}
        meta={`${usuarios.length} cuentas · ${metricas.length} métricas · ${fuentes.length} fuentes`}
        className="-mx-5 -mt-5 mb-5"
      />

      <UsuariosPanel id={ID_USUARIOS} usuarios={usuarios} />

      <CatalogoPanel
        id={ID_CATALOGO}
        metricas={metricas}
        usuarios={usuarios}
        onAgregar={() => setDialogo({ modo: 'alta' })}
        onEditar={(metrica) => setDialogo({ modo: 'edicion', metrica })}
      />

      <FuentesPanel
        id={ID_FUENTES}
        fuentes={fuentes}
        metricas={metricas}
        cfg={cfgDeepLinks}
        now={ahora}
        onIrAConfig={() => irA(ID_DEEP_LINKS)}
      />

      <DeepLinksPanel id={ID_DEEP_LINKS} servidor={cfgDeepLinks} />

      <MetricaDialog
        open={dialogo.modo !== 'cerrado'}
        metrica={dialogo.modo === 'edicion' ? dialogo.metrica : null}
        metricas={metricas}
        usuarios={usuarios}
        onClose={() => setDialogo({ modo: 'cerrado' })}
      />
    </>
  )
}

/** Veredicto en palabras: el color de la TriageBar nunca comunica solo. */
export function titularDe(criticos: number, atencion: number): string {
  if (criticos === 0 && atencion === 0) {
    return 'La configuración está completa'
  }
  const partes: string[] = []
  if (criticos > 0) {
    partes.push(
      `${criticos} ${criticos === 1 ? 'cosa rota' : 'cosas rotas'} en la configuración`,
    )
  }
  if (atencion > 0) {
    partes.push(`${atencion} sin terminar`)
  }
  return partes.join(' y ')
}
